/**
 * Negative Space Engine — whitespace as a design material.
 *
 * Decides how much of the canvas must stay empty and guards against
 * crowding. Targets are expressed as empty-canvas ratios (0–1) that the
 * adapter converts to spacing density, and that a future scorer can verify
 * against the Design Engine's whitespace metric.
 */

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Compute negative-space targets from style + mood.
 * Returns { targetEmptyRatio, minEmptyRatio, maxContentCoverage,
 *           breathingRoom, assessment guidance }.
 */
export const planNegativeSpace = ({ style, mood }) => {
  // Whitespace bias is the style's non-negotiable floor; sophistication
  // raises the target, energy trades it away for density.
  const targetEmptyRatio = Number(clamp(
    0.35 + style.whitespaceBias * 0.3 + mood.sophistication * 0.1 - mood.energy * 0.12,
    0.35, 0.75
  ).toFixed(2));

  const minEmptyRatio = Number((targetEmptyRatio - 0.1).toFixed(2));

  return {
    targetEmptyRatio,
    minEmptyRatio,
    maxContentCoverage: Number((1 - minEmptyRatio).toFixed(2)),
    breathingRoom: style.whitespaceBias >= 0.75 ? 'generous' : style.whitespaceBias >= 0.5 ? 'balanced' : 'tight',
  };
};

/**
 * Classify an actual empty-space ratio against a plan.
 * Returns { status: 'crowded'|'balanced'|'sparse', ok }.
 * `ok` is false only for crowding — sparse is a stylistic choice, crowded
 * is a defect.
 */
export const assessNegativeSpace = (emptyRatio, plan) => {
  if (emptyRatio < plan.minEmptyRatio) return { status: 'crowded', ok: false };
  if (emptyRatio > plan.targetEmptyRatio + 0.15) return { status: 'sparse', ok: true };
  return { status: 'balanced', ok: true };
};
